// src/services/unreadService.ts
import { logger } from '../utils/logger';

import { supabase } from './supabase';

export interface UnreadCount {
  matchId: string;
  count: number;
}

export interface GroupUnreadCount {
  groupId: string;
  count: number;
}

export interface TotalUnreadCounts {
  directMessages: number;
  groupMessages: number;
  total: number;
}

// Cache for unread counts with 30-second TTL
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL = 30 * 1000; // 30 seconds
const unreadCountsCache = new Map<string, CacheEntry<UnreadCount[]>>();
const groupUnreadCountsCache = new Map<string, CacheEntry<GroupUnreadCount[]>>();

function isCacheValid<T>(entry: CacheEntry<T> | undefined): boolean {
  return !!entry && (Date.now() - entry.timestamp) < CACHE_TTL;
}

function getFromCache<T>(cache: Map<string, CacheEntry<T>>, key: string): T | null {
  const entry = cache.get(key);
  if (isCacheValid(entry)) {
    return entry!.data;
  }
  cache.delete(key);
  return null;
}

function setCache<T>(cache: Map<string, CacheEntry<T>>, key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

// Debounce map for mark-as-read operations
const markAsReadDebounce = new Map<string, NodeJS.Timeout>();

/**
 * Get unread counts for all 1:1 matches (with caching)
 */
export async function getAllUnreadCounts(skipCache = false): Promise<{ success: boolean; counts?: UnreadCount[]; error?: string }> {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      return { success: false, error: `Auth error: ${authError.message}` };
    }
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Check cache
    const cacheKey = `direct:${user.id}`;
    if (!skipCache) {
      const cached = getFromCache(unreadCountsCache, cacheKey);
      if (cached) return { success: true, counts: cached };
    }

    // Get all matches for the user
    const { data: matches, error: matchError } = await supabase
      .from('matches')
      .select('id')
      .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
      .eq('status', 'accepted');

    if (matchError) {
      return { success: false, error: matchError.message };
    }

    if (!matches || matches.length === 0) {
      setCache(unreadCountsCache, cacheKey, []);
      return { success: true, counts: [] };
    }

    // Get unread counts for each match
    const matchIds = matches.map(m => m.id);
    const { data: unreadMessages, error: messageError } = await supabase
      .from('messages')
      .select('match_id')
      .in('match_id', matchIds)
      .neq('sender_id', user.id)
      .eq('read', false);

    if (messageError) {
      return { success: false, error: messageError.message };
    }

    // Count messages per match
    const countMap = new Map<string, number>();
    unreadMessages?.forEach(msg => {
      countMap.set(msg.match_id, (countMap.get(msg.match_id) || 0) + 1);
    });

    const counts: UnreadCount[] = Array.from(countMap.entries()).map(([matchId, count]) => ({
      matchId,
      count
    }));

    setCache(unreadCountsCache, cacheKey, counts);
    return { success: true, counts };

  } catch (error) {
    logger.error('Error in getAllUnreadCounts:', error);
    return { success: false, error: 'Failed to get unread counts' };
  }
}

/**
 * Get unread counts for all groups using last_read_at timestamp (optimized with parallel queries)
 */
export async function getAllGroupUnreadCounts(skipCache = false): Promise<{ success: boolean; counts?: GroupUnreadCount[]; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Check cache
    const cacheKey = `group:${user.id}`;
    if (!skipCache) {
      const cached = getFromCache(groupUnreadCountsCache, cacheKey);
      if (cached) return { success: true, counts: cached };
    }

    // Get all groups user is a member of WITH their last_read_at timestamp
    const { data: groupMembers, error: memberError } = await supabase
      .from('group_members')
      .select('group_id, last_read_at')
      .eq('user_id', user.id);

    if (memberError) {
      return { success: false, error: memberError.message };
    }

    if (!groupMembers || groupMembers.length === 0) {
      setCache(groupUnreadCountsCache, cacheKey, []);
      return { success: true, counts: [] };
    }

    // Count unread messages for all groups IN PARALLEL
    const countPromises = groupMembers.map(async (member) => {
      const lastReadAt = member.last_read_at || '1970-01-01';

      const { count, error } = await supabase
        .from('group_messages')
        .select('*', { count: 'exact', head: true })
        .eq('group_id', member.group_id)
        .neq('sender_id', user.id)
        .gt('created_at', lastReadAt);

      if (error) {
        logger.error(`Error counting unread for group ${member.group_id}:`, error);
        return null;
      }

      if (count && count > 0) {
        return {
          groupId: member.group_id,
          count
        };
      }

      return null;
    });

    const results = await Promise.all(countPromises);
    const counts: GroupUnreadCount[] = results.filter((c): c is GroupUnreadCount => c !== null);

    setCache(groupUnreadCountsCache, cacheKey, counts);
    return { success: true, counts };

  } catch (error) {
    logger.error('Error in getAllGroupUnreadCounts:', error);
    return { success: false, error: 'Failed to get group unread counts' };
  }
}

/**
 * Mark all group messages as read by updating last_read_at timestamp (with debouncing)
 */
export async function markGroupMessagesAsRead(
  groupId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Debounce mark-as-read operations per group
    const debounceKey = `${user.id}:${groupId}`;
    const existingTimer = markAsReadDebounce.get(debounceKey);

    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    return new Promise((resolve) => {
      const timer = setTimeout(async () => {
        markAsReadDebounce.delete(debounceKey);

        const now = new Date().toISOString();

        // Update last_read_at
        const { error: updateError } = await supabase
          .from('group_members')
          .update({ last_read_at: now })
          .eq('group_id', groupId)
          .eq('user_id', user.id);

        if (updateError) {
          resolve({ success: false, error: updateError.message });
          return;
        }

        // Clear cache to force refresh
        const cacheKey = `group:${user.id}`;
        groupUnreadCountsCache.delete(cacheKey);

        resolve({ success: true });
      }, 500); // 500ms debounce

      markAsReadDebounce.set(debounceKey, timer);
    });

  } catch (error) {
    logger.error('Error in markGroupMessagesAsRead:', error);
    return { success: false, error: 'Failed to mark group messages as read' };
  }
}

/**
 * Get total unread message count across all matches and groups
 */
export async function getTotalUnreadCount(skipCache = false): Promise<{ success: boolean; counts?: TotalUnreadCounts; error?: string }> {
  try {
    const [directResult, groupResult] = await Promise.all([
      getAllUnreadCounts(skipCache),
      getAllGroupUnreadCounts(skipCache)
    ]);

    if (!directResult.success) {
      return { success: false, error: directResult.error };
    }

    if (!groupResult.success) {
      return { success: false, error: groupResult.error };
    }

    const directMessages = directResult.counts?.reduce((sum, item) => sum + item.count, 0) || 0;
    const groupMessages = groupResult.counts?.reduce((sum, item) => sum + item.count, 0) || 0;

    return {
      success: true,
      counts: {
        directMessages,
        groupMessages,
        total: directMessages + groupMessages
      }
    };
  } catch (error) {
    logger.error('Error in getTotalUnreadCount:', error);
    return { success: false, error: 'Failed to get total unread count' };
  }
}

/**
 * Clear all caches (useful for debugging or forced refresh)
 */
export function clearUnreadCache() {
  unreadCountsCache.clear();
  groupUnreadCountsCache.clear();
}

/**
 * Subscribe to unread count changes (both direct and group messages)
 */
export function subscribeToUnreadChanges(callback: () => void) {
  let debounceTimer: NodeJS.Timeout | null = null;

  // Debounced callback to avoid excessive updates
  const debouncedCallback = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(() => {
      // Clear caches on any change
      clearUnreadCache();
      callback();
    }, 500); // Increased from 300ms to 500ms for better batching
  };

  // Subscribe to direct messages
  const directChannel = supabase
    .channel('unread-direct-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'messages'
      },
      debouncedCallback
    )
    .subscribe();

  // Subscribe to group messages
  const groupChannel = supabase
    .channel('unread-group-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'group_messages'
      },
      debouncedCallback
    )
    .subscribe();

  // Subscribe to group_members changes (for last_read_at updates)
  const groupMembersChannel = supabase
    .channel('unread-group-members-changes')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'group_members'
      },
      debouncedCallback
    )
    .subscribe();

  // Return cleanup function
  return () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    supabase.removeChannel(directChannel);
    supabase.removeChannel(groupChannel);
    supabase.removeChannel(groupMembersChannel);

    // Clear any pending mark-as-read operations
    markAsReadDebounce.forEach(timer => clearTimeout(timer));
    markAsReadDebounce.clear();
  };
}